import { Link } from 'react-router-dom';
import { Truck, CreditCard, ShoppingCart, CheckCircle, ArrowRight, Star, Phone, CalendarClock, Package, Users, Headphones, MessageCircle } from 'lucide-react';

const TRUCK_IMG = "https://customer-assets.emergentagent.com/job_rainforest-portal/artifacts/oa3385bi_AMAZONIA%20-%20Livraison%20Bruxelles.jpg";
const WAITRESS_IMG = "https://customer-assets.emergentagent.com/job_rainforest-portal/artifacts/5pihlob1_AMAZONIA%20-%20PRO%20-%20Waitress%20-%20Logo.jpg";

export default function AvantagesPro() {
  return (
    <div data-testid="avantages-pro-page">
      {/* Hero with truck background */}
      <section className="relative min-h-[70vh] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img src={TRUCK_IMG} alt="Livraison Amazonia Bruxelles" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#111]/90 via-[#111]/75 to-[#111]/40" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full py-20">
          <div className="max-w-2xl">
            <p className="text-xs uppercase tracking-[0.3em] text-[#F5C400] font-semibold mb-4">
              Avantages professionnels
            </p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white leading-none mb-6 tracking-tight">
              AMAZONIA.FOOD<br />
              <span className="text-[#00A5FF]">La solution intelligente</span><br />
              et pratique
            </h1>
            <p className="text-base text-white/70 leading-relaxed mb-8 max-w-lg">
              Importation et distribution de produits alimentaires latino-américains dans tout le Benelux. 
            </p>

            {/* 4 bullets */}
            <div className="grid grid-cols-2 gap-3 mb-10">
              {[
                { icon: ShoppingCart, label: 'Commande simplifiée' },
                { icon: Truck, label: 'Livraison rapide' },
                { icon: CreditCard, label: 'Facilité de paiement' },
                { icon: Headphones, label: 'Contact privilégié' },
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-2.5">
                  <div className="w-8 h-8 bg-white/10 flex items-center justify-center shrink-0">
                    <item.icon size={14} className="text-[#00A5FF]" />
                  </div>
                  <span className="text-sm text-white/80 font-medium">{item.label}</span>
                </div>
              ))}
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-wrap gap-3">
              <a
                href="https://calendly.com/question-amazonia/30min"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hero-calendly-cta"
                className="inline-flex items-center gap-2 px-7 py-3.5 bg-[#F5C400] text-[#111] font-bold text-sm rounded-sm hover:-translate-y-1 transition-all duration-200"
              >
                <Phone size={16} />
                Prendre rendez-vous avec notre commercial
              </a>
              <a
                href="https://wa.me/32489102365"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hero-whatsapp-cta"
                className="inline-flex items-center gap-2 px-7 py-3.5 bg-[#25D366] text-white font-bold text-sm rounded-sm hover:-translate-y-1 transition-all duration-200"
              >
                <MessageCircle size={16} />
                WhatsApp
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* 4 Main Advantages */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="text-center mb-14">
          <p className="text-xs uppercase tracking-[0.3em] text-[#E8231B] font-semibold mb-3">Pourquoi AMAZONIA.FOOD ?</p>
          <h2 className="text-3xl sm:text-4xl font-extrabold text-[#111] tracking-tight">
            Quatre avantages décisifs
          </h2>
        </div>

        <div className="grid md:grid-cols-2 gap-0 border border-black/10">
          {/* Advantage 1 — Commande simplifiée */}
          <div className="p-8 lg:p-10 border-b md:border-r border-black/10">
            <div className="w-14 h-14 bg-[#00A5FF]/10 flex items-center justify-center mb-6">
              <Star size={28} className="text-[#00A5FF]" />
            </div>
            <h3 className="text-xl font-extrabold text-[#111] mb-3 tracking-tight">
              Commande simplifiée
            </h3>
            <p className="text-sm text-black/60 leading-relaxed">
              Vos produits favoris accessibles en un clic, sur base de vos anciennes commandes fournisseurs, au <strong className="text-[#111]">tarif préférentiel</strong>.
            </p>
            <div className="mt-6 pt-6 border-t border-black/5 space-y-2">
              <div className="flex items-start gap-2">
                <CheckCircle size={14} className="text-[#00A5FF] mt-0.5 shrink-0" />
                <p className="text-xs text-black/50">Catalogue personnalisé selon votre profil</p>
              </div>
              <div className="flex items-start gap-2">
                <CheckCircle size={14} className="text-[#00A5FF] mt-0.5 shrink-0" />
                <p className="text-xs text-black/50">Recommandation automatique</p>
              </div>
              <div className="flex items-start gap-2">
                <CheckCircle size={14} className="text-[#00A5FF] mt-0.5 shrink-0" />
                <p className="text-xs text-black/50">Récommandez en un clic</p>
              </div>
            </div>
          </div>

          {/* Advantage 2 — Livraison rapide */}
          <div className="p-8 lg:p-10 border-b border-black/10">
            <div className="w-14 h-14 bg-[#E8231B]/10 flex items-center justify-center mb-6">
              <Truck size={28} className="text-[#E8231B]" />
            </div>
            <h3 className="text-xl font-extrabold text-[#111] mb-3 tracking-tight">
              Livraison rapide et pratique
            </h3>
            <p className="text-sm text-black/60 leading-relaxed">
              Livraison selon <strong className="text-[#111]">votre convenance</strong>. Nous nous chargeons de toute la logistique, vous ne bougez pas de chez vous !
            </p>
            <div className="mt-6 pt-6 border-t border-black/5 space-y-2">
              <div className="flex items-start gap-2">
                <CheckCircle size={14} className="text-[#E8231B] mt-0.5 shrink-0" />
                <p className="text-xs text-black/50">Livraison dans tout le Benelux</p>
              </div>
              <div className="flex items-start gap-2">
                <CheckCircle size={14} className="text-[#E8231B] mt-0.5 shrink-0" />
                <p className="text-xs text-black/50">Logistique intégralement gérée</p>
              </div>
              <div className="flex items-start gap-2">
                <CheckCircle size={14} className="text-[#E8231B] mt-0.5 shrink-0" />
                <p className="text-xs text-black/50">Créneau selon vos disponibilités</p>
              </div>
            </div>
          </div>

          {/* Advantage 3 — Facilité de paiement */}
          <div className="p-8 lg:p-10 md:border-r border-black/10">
            <div className="w-14 h-14 bg-[#F5C400]/15 flex items-center justify-center mb-6">
              <CreditCard size={28} className="text-[#F5C400]" />
            </div>
            <h3 className="text-xl font-extrabold text-[#111] mb-3 tracking-tight">
              Facilité de paiement
            </h3>
            <p className="text-sm text-black/60 leading-relaxed">
              Payez quand vous avez réalisé les <strong className="text-[#111]">bénéfices auprès de vos clients</strong>. Des conditions de paiement pensées pour votre trésorerie.
            </p>
            <div className="mt-6 pt-6 border-t border-black/5 space-y-2">
              <div className="flex items-start gap-2">
                <CheckCircle size={14} className="text-[#F5C400] mt-0.5 shrink-0" />
                <p className="text-xs text-black/50">10% d'acompte à la commande</p>
              </div>
              <div className="flex items-start gap-2">
                <CheckCircle size={14} className="text-[#F5C400] mt-0.5 shrink-0" />
                <p className="text-xs text-black/50">10% à la livraison</p>
              </div>
              <div className="flex items-start gap-2">
                <CheckCircle size={14} className="text-[#F5C400] mt-0.5 shrink-0" />
                <p className="text-xs text-black/50">80% à 30 jours</p>
              </div>
            </div>
          </div>

          {/* Advantage 4 — Contact privilégié */}
          <div className="p-8 lg:p-10">
            <div className="w-14 h-14 bg-[#00A5FF]/10 flex items-center justify-center mb-6">
              <Headphones size={28} className="text-[#00A5FF]" />
            </div>
            <h3 className="text-xl font-extrabold text-[#111] mb-3 tracking-tight">
              Contact privilégié
            </h3>
            <p className="text-sm text-black/60 leading-relaxed">
              Vous avez un <strong className="text-[#111]">numéro direct dédié</strong> au suivi de vos commandes. On vous décharge de la galère de trouver les fournitures. Vous faciliter la vie, c'est notre métier ! Le vôtre, c'est de vous occuper de votre client.
            </p>
            <div className="mt-6 pt-6 border-t border-black/5 space-y-2">
              <div className="flex items-start gap-2">
                <CheckCircle size={14} className="text-[#00A5FF] mt-0.5 shrink-0" />
                <p className="text-xs text-black/50">Ligne directe pour le suivi de commande</p>
              </div>
              <div className="flex items-start gap-2">
                <CheckCircle size={14} className="text-[#00A5FF] mt-0.5 shrink-0" />
                <p className="text-xs text-black/50">Interlocuteur dédié</p>
              </div>
              <div className="flex items-start gap-2">
                <CheckCircle size={14} className="text-[#00A5FF] mt-0.5 shrink-0" />
                <p className="text-xs text-black/50">Réactivité garantie</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* "Vendez d'abord, payez ensuite !" Section */}
      <section className="bg-[#F8F9FA] border-t border-b border-black/10">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2">
            {/* Image */}
            <div className="relative overflow-hidden bg-[#F8F9FA] flex items-center justify-center p-4 md:p-8">
              <img src={WAITRESS_IMG} alt="Professionnelle Amazonia" className="w-full h-auto object-contain max-h-[700px]" />
            </div>
            {/* Content */}
            <div className="p-8 md:p-12 lg:p-16 flex flex-col justify-center">
              <p className="text-xs uppercase tracking-[0.3em] text-[#E8231B] font-semibold mb-3">Avantage financier</p>
              <h2 className="text-3xl sm:text-4xl font-extrabold text-[#111] tracking-tight mb-6 leading-tight">
                Vendez d'abord,<br />payez ensuite !
              </h2>
              <p className="text-base text-black/60 leading-relaxed mb-8">
                Notre modèle de paiement fractionné est conçu pour protéger votre trésorerie. Vous ne payez la majorité qu'après avoir vendu les produits à vos propres clients.
              </p>

              {/* Payment Timeline */}
              <div className="space-y-0 mb-8">
                <div className="relative pl-12 pb-6 border-l-2 border-[#00A5FF]/30">
                  <div className="absolute left-0 top-0 -translate-x-1/2 w-8 h-8 bg-[#00A5FF] rounded-full flex items-center justify-center">
                    <ShoppingCart size={14} className="text-white" />
                  </div>
                  <div className="pt-0.5">
                    <p className="text-xs uppercase tracking-wider text-[#00A5FF] font-semibold mb-0.5">A la commande</p>
                    <p className="text-2xl font-extrabold text-[#111]">10%</p>
                    <p className="text-xs text-black/40">Acompte de confirmation</p>
                  </div>
                </div>
                <div className="relative pl-12 pb-6 border-l-2 border-[#F5C400]/30">
                  <div className="absolute left-0 top-0 -translate-x-1/2 w-8 h-8 bg-[#F5C400] rounded-full flex items-center justify-center">
                    <Truck size={14} className="text-[#111]" />
                  </div>
                  <div className="pt-0.5">
                    <p className="text-xs uppercase tracking-wider text-[#F5C400] font-semibold mb-0.5">A la livraison</p>
                    <p className="text-2xl font-extrabold text-[#111]">10%</p>
                    <p className="text-xs text-black/40">Règlement à réception</p>
                  </div>
                </div>
                <div className="relative pl-12">
                  <div className="absolute left-0 top-0 -translate-x-1/2 w-8 h-8 bg-[#E8231B] rounded-full flex items-center justify-center">
                    <CalendarClock size={14} className="text-white" />
                  </div>
                  <div className="pt-0.5">
                    <p className="text-xs uppercase tracking-wider text-[#E8231B] font-semibold mb-0.5">30 jours plus tard</p>
                    <p className="text-2xl font-extrabold text-[#111]">80%</p>
                    <p className="text-xs text-black/40">Quand vous avez réalisé vos bénéfices</p>
                  </div>
                </div>
              </div>

              <p className="text-sm text-black/50 italic">
                "Vous commandez, vous vendez, et vous nous payez quand c'est fait."
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Who is it for */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="text-center mb-12">
          <p className="text-xs uppercase tracking-[0.3em] text-[#00A5FF] font-semibold mb-3">Pour qui ?</p>
          <h2 className="text-3xl sm:text-4xl font-extrabold text-[#111] tracking-tight">
            Nous livrons les professionnels du Benelux
          </h2>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-black/10 border border-black/10">
          {[
            { icon: ShoppingCart, title: 'Magasins', desc: "Supermarchés, épiceries latines, boutiques alimentaires" },
            { icon: Users, title: 'Restauration', desc: "Restaurants, bars, brasseries, cantines" },
            { icon: Package, title: 'Traiteurs', desc: "Services traiteur, catering, food trucks" },
            { icon: Star, title: 'Stands Festival', desc: "Événementiel, marchés, foires gastronomiques" },
          ].map((item, i) => (
            <div key={i} className="bg-white p-6 lg:p-8 text-center">
              <div className="w-14 h-14 bg-[#F8F9FA] mx-auto flex items-center justify-center mb-4">
                <item.icon size={24} className="text-[#00A5FF]" />
              </div>
              <h3 className="text-sm font-extrabold text-[#111] mb-2">{item.title}</h3>
              <p className="text-xs text-black/50 leading-relaxed">{item.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Final CTA with Calendly */}
      <section className="bg-[#111]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-center mb-8">
            <p className="text-xs uppercase tracking-[0.3em] text-[#F5C400] font-semibold mb-3">Prêt à commencer ?</p>
            <h2 className="text-2xl sm:text-3xl font-extrabold text-white tracking-tight mb-4">
              Prenez rendez-vous avec notre commercial
            </h2>
            <p className="text-base text-white/60 max-w-lg mx-auto">
              Activez votre compte professionnel et accédez à tous les avantages AMAZONIA.FOOD.
            </p>
          </div>
          <div className="bg-white rounded-sm overflow-hidden" data-testid="calendly-bottom">
            <iframe
              src="https://calendly.com/question-amazonia/30min?primary_color=00a5ff&hide_gdpr_banner=1"
              width="100%"
              height="630"
              frameBorder="0"
              title="Calendly"
              className="border-0"
            />
          </div>
        </div>
      </section>
    </div>
  );
}
